import { MonetaryAmount, TagPill } from '@components'
import { getDocumentsPageUrl, getExpenseClaimPageUrl } from '@constants'
import { ExpenseClaimLine, ExpenseClaimLineKind, IPurchaseInvoice } from '@query'
import { formatDate, formatEurosWithOpts } from '@utils'
import {
  RelationContent,
  RelationGroupFooter,
  RelationIcon,
  RelationListWrapper,
  RelationRowPrimary,
  RelationRowSecondary
} from '@pages/DocumentPage/DocumentForm/RelationMapper.styled'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight, FaChevronDown, FaReceipt } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { AttachmentSelector } from '@containers/AttachmentSelector'
import { PurchaseInvoiceStatusBadge } from './InvoiceStatusBadge'
import { AttachmentSection, DetailSection, SectionLabel } from './InvoicingDetails.styled'

type ExpenseClaimPayload = NonNullable<IPurchaseInvoice['expense_claim']>

type Props = {
  slug: string
  invoice: IPurchaseInvoice
  expenseClaim: ExpenseClaimPayload
  showDocumentLink: boolean
}

type LineGroup = 'mileage' | 'perDiem' | 'expense'

const LINE_GROUP_ORDER: LineGroup[] = ['mileage', 'perDiem', 'expense']

const lineGroup = (kind: ExpenseClaimLineKind): LineGroup => {
  if (kind === ExpenseClaimLineKind.MILEAGE) {
    return 'mileage'
  }
  if (kind === ExpenseClaimLineKind.EXPENSE) {
    return 'expense'
  }
  return 'perDiem'
}

const themeMode = 'light' as const

export const PurchaseInvoiceExpenseClaimSection: React.FC<Props> = ({
  slug,
  invoice,
  expenseClaim,
  showDocumentLink
}) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(true)

  const approverName = expenseClaim.approver?.name || expenseClaim.approver_name || null
  const approvedDate = expenseClaim.approved_at ? formatDate(expenseClaim.approved_at) : null

  const groupedLines = useMemo(() => {
    const groups = new Map<LineGroup, ExpenseClaimLine[]>()
    for (const line of expenseClaim.lines) {
      const group = lineGroup(line.kind)
      groups.set(group, [...(groups.get(group) ?? []), line])
    }
    return LINE_GROUP_ORDER.filter(group => groups.has(group)).map(
      group => [group, groups.get(group) ?? []] as const
    )
  }, [expenseClaim.lines])

  const linesTotal = useMemo(
    () => expenseClaim.lines.reduce((sum, line) => sum + line.amount, 0),
    [expenseClaim.lines]
  )

  const counterAccountLabel = expenseClaim.counter_account
    ? `${expenseClaim.counter_account.number} ${expenseClaim.counter_account.name}`
    : t('invoicing.purchaseInvoiceDetails.counterAccountPayables')

  const bookingLabel = expenseClaim.approved_as_accrual_based
    ? t('invoicing.purchaseInvoiceDetails.bookingAccrual', {
        date: expenseClaim.booking_date ? formatDate(expenseClaim.booking_date) : '—'
      })
    : t('invoicing.purchaseInvoiceDetails.bookingCash')

  const showVoucherLink =
    showDocumentLink &&
    Boolean(expenseClaim.approved_as_accrual_based) &&
    expenseClaim.document_id != null

  const attachmentIds = expenseClaim.attachment_items.map(item => item.id)

  return (
    <>
      <DetailSection>
        <SectionLabel>{t('invoicing.purchaseInvoiceDetails.createdFromClaim')}</SectionLabel>
        <RelationListWrapper $themeMode={themeMode}>
          <ToggleRow
            type="button"
            aria-expanded={isOpen}
            aria-label={
              isOpen
                ? t('invoicing.purchaseInvoiceDetails.collapseClaim')
                : t('invoicing.purchaseInvoiceDetails.expandClaim')
            }
            onClick={() => setIsOpen(value => !value)}
          >
            <RelationIcon $color={theme.colors.warning}>
              <FaReceipt />
            </RelationIcon>
            <RelationContent>
              <RelationRowPrimary $themeMode={themeMode}>
                {t('invoicing.purchaseInvoiceDetails.claimRowTitle', {
                  number: expenseClaim.number,
                  date: approvedDate ?? '—'
                })}
              </RelationRowPrimary>
              <RelationRowSecondary>
                {invoice.amount != null ? <MonetaryAmount value={invoice.amount} /> : null}
                {invoice.amount != null && invoice.sender_name ? <span>·</span> : null}
                {invoice.sender_name ? <span>{invoice.sender_name}</span> : null}
              </RelationRowSecondary>
            </RelationContent>
            <StatusColumn>
              <PurchaseInvoiceStatusBadge isPaid={invoice.is_paid} size="md" />
              <ChevronHitArea
                animate={{ rotateZ: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaChevronDown color={theme.colors.iconGray} />
              </ChevronHitArea>
            </StatusColumn>
          </ToggleRow>

          <AnimatePresence initial={false}>
            {isOpen ? (
              <ExpandedPanel
                key="expense-claim-expanded"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FactsGrid>
                  <FactCell>
                    <FactLabel>{t('invoicing.purchaseInvoiceDetails.approverCombined')}</FactLabel>
                    <FactValue>{approverName ?? '—'}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('invoicing.purchaseInvoiceDetails.approvedAt')}</FactLabel>
                    <FactValue>{approvedDate ?? '—'}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('invoicing.purchaseInvoiceDetails.booking')}</FactLabel>
                    <FactValue>{bookingLabel}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('invoicing.purchaseInvoiceDetails.counterAccount')}</FactLabel>
                    <FactValue title={counterAccountLabel}>{counterAccountLabel}</FactValue>
                  </FactCell>
                </FactsGrid>

                {groupedLines.map(([group, lines]) => (
                  <LineGroupBlock key={group}>
                    <LineGroupLabel>
                      {t(`invoicing.purchaseInvoiceDetails.lineGroups.${group}`)}
                    </LineGroupLabel>
                    {lines.map(line => (
                      <ClaimLineRow key={line.id} line={line} />
                    ))}
                  </LineGroupBlock>
                ))}

                <TotalsBlock>
                  <TotalLine>
                    <span>{t('invoicing.purchaseInvoiceDetails.amountPayable')}</span>
                    {invoice.amount != null ? (
                      <MonetaryAmount value={invoice.amount} />
                    ) : (
                      <span>{formatEurosWithOpts(linesTotal, { withEuroSymbol: true })}</span>
                    )}
                  </TotalLine>
                </TotalsBlock>
              </ExpandedPanel>
            ) : null}
          </AnimatePresence>

          <RelationGroupFooter $themeMode={themeMode}>
            <FooterLeft>
              {showVoucherLink && expenseClaim.document_id != null ? (
                <Link to={getDocumentsPageUrl(slug, { id: expenseClaim.document_id })}>
                  {t('invoicing.purchaseInvoiceDetails.openVoucher')}
                </Link>
              ) : null}
            </FooterLeft>
            <FooterLink to={getExpenseClaimPageUrl(slug, expenseClaim.id)}>
              {t('invoicing.purchaseInvoiceDetails.openClaim')}
              <FaArrowRight />
            </FooterLink>
          </RelationGroupFooter>
        </RelationListWrapper>
        <LockedNote>{t('invoicing.purchaseInvoiceDetails.expenseClaimLockedFields')}</LockedNote>
      </DetailSection>

      {attachmentIds.length > 0 && (
        <DetailSection>
          <SectionLabel>
            {t('invoicing.purchaseInvoiceDetails.attachmentsCount', {
              count: attachmentIds.length
            })}
          </SectionLabel>
          <AttachmentSection>
            <AttachmentSelector value={attachmentIds} disabled={true} />
          </AttachmentSection>
        </DetailSection>
      )}
    </>
  )
}

const ClaimLineRow: React.FC<{ line: ExpenseClaimLine }> = ({ line }) => (
  <LineRow>
    <LineMain>
      <LineName title={line.description}>{line.description}</LineName>
      {line.basis ? <LineBasis>{line.basis}</LineBasis> : null}
      {line.tags.length > 0 ? (
        <LineTags>
          {line.tags.map(tag => (
            <TagPill
              key={tag.id}
              tag={{ id: tag.id, name: tag.name ?? '', color: tag.color ?? undefined }}
            />
          ))}
        </LineTags>
      ) : null}
    </LineMain>
    <LineAmount>{formatEurosWithOpts(line.amount, { withEuroSymbol: true })}</LineAmount>
  </LineRow>
)

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  font: inherit;
  text-align: left;
  color: inherit;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const StatusColumn = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-shrink: 0;
`

const ChevronHitArea = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
`

const ExpandedPanel = styled(motion.div)`
  overflow: hidden;
`

const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.defaultHover};
`

const FactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const FactLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const FactValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const LineGroupBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const LineGroupLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.iconGray};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const LineRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const LineMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 1;
  min-width: 0;
`

const LineName = styled.span`
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const LineBasis = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const LineTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const LineAmount = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  flex-shrink: 0;
`

const TotalsBlock = styled.div`
  padding: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-variant-numeric: tabular-nums;
`

const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 700;
`

const FooterLeft = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FooterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  flex-shrink: 0;

  svg {
    width: 0.6875rem;
    height: 0.6875rem;
  }

  &:hover {
    text-decoration: none;
  }
`

const LockedNote = styled.p`
  margin: ${({ theme }) => theme.spacing.xs}rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.45;
`
